import type { InputHTMLAttributes, JSX, ReactNode } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from '../i18n';
import { Icon } from './Icon';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  /** Подсказка или пояснение под полем. */
  hint?: ReactNode;
  /** Текст ошибки. Показывается вместо подсказки. */
  error?: string | undefined;
  /** Моноширинный ввод — для адресов, портов, путей (§12). */
  mono?: boolean;
  /**
   * Кнопка-глаз, показывающая набранный пароль.
   *
   * Показывает **набираемое**, а не хранимое: секреты устройств платформа
   * наружу не отдаёт вовсе (§10.2), и раскрыть уже сохранённый пароль
   * нечем — его нет ни в одном ответе API. Смысл кнопки в другом:
   * пароль VNC вводят с бумажки, ошибаются в раскладке и в регистре,
   * а узнают об этом через минуту от сервера, отказавшего в подключении.
   */
  reveal?: boolean;
}

/**
 * Поле ввода по макету NetDeck.html: подпись 12px над полем высотой 34px,
 * рамка `--nd-border-strong`, фокус — синий контур с мягким ореолом.
 *
 * Подпись и ошибка под полем растягивают поле по высоте. В форме, собранной
 * в столбец, это безразлично; в форме, выстроенной в строку, — нет, поэтому
 * такие формы собираются из `FormRow` и `FormActions`, а не из голого flex.
 */
export function Field({
  label,
  hint,
  error,
  mono = false,
  reveal = false,
  ...input
}: FieldProps): JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const describedBy = error || hint ? `${id}-hint` : undefined;

  const [shown, setShown] = useState(false);
  // Раскрывать имеет смысл только пароль: у остальных полей и так всё видно.
  const revealable = reveal && input.type === 'password';

  // Подпись — отдельный элемент со ссылкой на поле, а не обёртка вокруг него.
  // При обёртке доступным именем поля становится весь текст внутри `label`,
  // то есть подпись вместе с подсказкой: скринридер прочитал бы
  // «Электронная почта только латиница в имени ящика». Подсказка должна
  // приходить отдельно, через aria-describedby.
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm text-muted" htmlFor={id}>
        {label}
      </label>
      <div className="relative flex">
        <input
          {...input}
          type={revealable && shown ? 'text' : input.type}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={[
            'h-[34px] w-full rounded-sm border bg-raised px-2.5 text-base text-fg',
            'placeholder:text-faint focus:outline-none',
            mono ? 'font-mono' : '',
            revealable ? 'pr-9' : '',
            error ? 'border-down' : 'border-line-strong',
          ].join(' ')}
        />

        {revealable ? (
          <button
            type="button"
            // Кнопка внутри поля не должна забирать фокус табуляцией:
            // между полем и следующим полем ей не место, а нажимают её мышью.
            tabIndex={-1}
            aria-pressed={shown}
            aria-label={shown ? t('common.action.hidePassword') : t('common.action.showPassword')}
            title={shown ? t('common.action.hidePassword') : t('common.action.showPassword')}
            onClick={() => setShown((value) => !value)}
            className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-dim hover:text-fg"
          >
            <Icon name={shown ? 'eyeOff' : 'eye'} />
          </button>
        ) : null}
      </div>
      {error ? (
        <span id={describedBy} className="text-sm text-down-fg">
          {error}
        </span>
      ) : hint ? (
        <span id={describedBy} className="font-mono text-xs text-dim">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Форма, выстроенная в строку.
 *
 * Элементы выравниваются по верхнему краю, а не по нижнему. Выравнивание
 * по нижнему краю кажется естественным — «поля стоят на одной линии», — но
 * ломается, как только у одного поля появляется подпись или ошибка: оно
 * становится выше, и его поле ввода всплывает над соседними.
 *
 * При выравнивании по верху подписи всех полей стоят на одной линии, а значит
 * и сами поля тоже; подсказка просто свисает вниз, ничего не сдвигая.
 */
export function FormRow({
  children,
  ...form
}: React.FormHTMLAttributes<HTMLFormElement>): JSX.Element {
  return (
    <form
      {...form}
      className="flex flex-wrap items-start gap-3 border-b border-line bg-row-alt px-3.5 py-3"
    >
      {children}
    </form>
  );
}

/**
 * Элемент строки формы, у которого нет собственной подписи, — галочка,
 * кнопка, короткое сообщение.
 *
 * Пустая подпись сверху — не костыль, а тот же элемент разметки, что и у полей:
 * благодаря ей элемент встаёт на одну линию с полями ввода без подгонки
 * отступов числом, которое разъедется при смене размера шрифта.
 */
export function FormAlign({
  children,
  title,
}: {
  children: ReactNode;
  title?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm" aria-hidden="true">
        &nbsp;
      </span>
      <div className="flex h-[34px] items-center gap-3" title={title}>
        {children}
      </div>
    </div>
  );
}

/** Кнопки в строке формы. Тот же выключатель выравнивания, что и `FormAlign`. */
export function FormActions({ children }: { children: ReactNode }): JSX.Element {
  return <FormAlign>{children}</FormAlign>;
}
